import { useQuery } from "@apollo/react-hooks";
import { Filters } from "@shopify/polaris";
import { isEmpty } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FilterNoSearchPolaris } from "../filters/FilterNoSearchPolaris";
import { QUERY_FULFILLMENTS } from "../order/FilterOrdersControlPolaris";
import {
    GET_ALL_TEAM_USERS_BY_ROLE,
    GET_ALL_TEAM_USERS_WITH_ROOT_USER,
    GET_USERS,
    TEAM_USERS_BY_ROLE,
} from "../../graphql/queries";
import { FilterHasSearchPolaris } from "../filters/FilterHasSearchPolaris";
import { TEAM_ROLE, USER_ROLE } from "../../variable";
import FilterSingleValue from "../filters/FilterSingleValue";
import { arrInvalid, checkRole, isPME_TEAM } from "../../helper";
import { useAppContext } from "../../context";

const STATUS_OPTIONS = ["Publish", "Draft"].map((i) => ({
    value: i,
    label: i,
}));

export const FilterProductBasePolaris = ({ filter, onChange }) => {
    const { currentUser } = useAppContext();
    const isPMETeam = isPME_TEAM(currentUser);
    const { isAdministrator } = checkRole(currentUser);

    // Props
    const search = filter?.search;
    const fulfillmentId = filter?.fulfillmentId;
    const statusProp = filter?.status;
    const supplierIdsProp = filter?.supplierIds;

    // State
    const [queryValue, setQueryValue] = useState(search);
    const [fulfillments, setFulfillments] = useState([]);
    const [fulfillment, setFulfillment] = useState({
        value: fulfillmentId,
        label: null,
        search: null,
    });
    const typingTimeoutRef = useRef(null);

    const [status, setStatus] = useState({
        value: statusProp,
        label: null,
        search: null,
    });

    const [teams, setTeams] = useState([]);
    const [team, setTeam] = useState({
        value: filter.teamId,
        label: null,
        search: null,
    });

    const [suppliers, setSuppliers] = useState([]);
    const [supplier, setSupplier] = useState({
        value: supplierIdsProp || null,
        label: null,
        search: null,
    });

    const {
        data: dataT,
        loading: loadingT,
        error: errorT,
    } = useQuery(GET_ALL_TEAM_USERS_WITH_ROOT_USER, {
        variables: {
            filter: {
                search: team.search || "",
            },
        },
        skip: !isAdministrator,
    });

    // query
    const { data, loading, error } = useQuery(QUERY_FULFILLMENTS);

    const { data: dataS } = useQuery(
        isAdministrator ? GET_USERS : TEAM_USERS_BY_ROLE,
        {
            variables: isAdministrator
                ? { filter: { role: USER_ROLE.Supplier } }
                : { role: TEAM_ROLE.Supplier },
        },
    );

    // Get data
    useEffect(() => {
        const newFF =
            data?.fulfillments?.length > 0
                ? data.fulfillments.map((f) => ({ value: f.id, label: f.name }))
                : [];
        setFulfillments(newFF);

        if (fulfillmentId != null) {
            const currentFF = newFF.find((i) => i.value === fulfillmentId);
            if (currentFF) {
                const { value, label } = currentFF;
                setFulfillment((prev) => ({
                    ...prev,
                    value,
                    label,
                }));
            }
        }
    }, [data, fulfillmentId]);
    //supplierIdsProp

    useEffect(() => {
        if (typeof statusProp !== "boolean") return;

        let state = {};
        if (statusProp) {
            state.value = "Publish";
            state.label = "Publish";
        } else {
            state.value = "Draft";
            state.label = "Draft";
        }

        setStatus((p) => ({ ...p, ...state }));
    }, [statusProp]);

    useEffect(() => {
        const nodes = getSuppliers(dataS, isAdministrator);
        setSuppliers(nodes);

        if (!arrInvalid(supplierIdsProp)) {
            const mapIds = supplierIdsProp.reduce((acc, item) => {
                acc[item] = true;
                return acc;
            }, {});

            const ids = [];
            const label = [];

            for (let node of nodes) {
                if (!node || !node.value) continue;

                if (mapIds[node.value]) {
                    ids.push(node.value);
                    label.push(node.label);
                }
            }

            if (ids.length > 0) {
                setSupplier((p) => ({ ...p, value: ids, label }));
            }
        }
    }, [dataS, supplierIdsProp, isAdministrator]);

    useEffect(() => {
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        if (onChange) {
            const sVal = status.value;
            const convertS = {
                All: null,
                Publish: true,
                Draft: false,
            };

            typingTimeoutRef.current = setTimeout(() => {
                onChange({
                    search: queryValue ? queryValue.trim() : queryValue,
                    fulfillmentId: fulfillment.value,
                    status: sVal ? convertS[sVal] : null,
                    teamId: team.value?.length > 0 ? team.value.trim() : null,
                    supplierIds:
                        supplier.value?.length > 0
                            ? supplier.value.filter(Boolean)
                            : null,
                });
            }, 500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryValue, fulfillment, status, team.value, supplier.value]);

    useEffect(() => {
        let newData =
            dataT?.getAllTeamUsersWithRootUser?.length > 0
                ? dataT.getAllTeamUsersWithRootUser
                      .map((tm) => {
                          let label = `${tm.name} - ${tm.id}`;
                          if (tm.createdBy) {
                              label = `${label} (${tm.createdBy})`;
                          }
                          return {
                              value: tm.id,
                              label,
                          };
                      })
                      .filter(Boolean)
                : [];

        setTeams(newData);
    }, [dataT]);

    const handleQueryValueChange = useCallback(
        (value) => {
            setQueryValue(value);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [queryValue],
    );

    // Actions
    const handleFFRemove = useCallback(
        () =>
            setFulfillment(() => ({ value: null, label: null, search: null })),
        [],
    );
    const handleQueryValueRemove = useCallback(() => setQueryValue(null), []);
    const handleStatusRemove = useCallback(() => {
        setStatus({ value: null, label: null, search: null });
    }, []);

    const handleTeamRemove = useCallback(
        () => setTeam(() => ({ value: null, label: null, search: null })),
        [],
    );

    const handleSupplierRemove = useCallback(
        () => setSupplier(() => ({ value: null, label: null, search: null })),
        [],
    );

    const clearAllField = [
        handleQueryValueRemove,
        handleFFRemove,
        handleStatusRemove,
        handleTeamRemove,
        handleSupplierRemove,
    ];

    const handleFiltersClearAll = useCallback(() => {
        for (let fn of clearAllField) {
            fn();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...clearAllField]);

    // Variables
    const filters = [
        isAdministrator
            ? {
                  key: "team",
                  label: "Teams",
                  filter: (
                      <FilterHasSearchPolaris
                          value={team}
                          options={teams}
                          loading={loadingT}
                          error={errorT}
                          onChange={({ value, label }) =>
                              setTeam((prev) => ({ ...prev, value, label }))
                          }
                          onChangeSearch={(search) =>
                              setTeam((prevState) => ({ ...prevState, search }))
                          }
                      />
                  ),
              }
            : null,
        {
            key: "fulfillment",
            label: "Fulfillment by",
            tagLabel: fulfillment.label,
            onRemove: handleFFRemove,
            filter: (
                <FilterNoSearchPolaris
                    data={fulfillments}
                    value={fulfillment}
                    loading={loading}
                    error={error}
                    onChange={({ value, label }) =>
                        setFulfillment((prevState) => ({
                            ...prevState,
                            value,
                            label,
                        }))
                    }
                    onChangeSearch={({ search }) =>
                        setFulfillment((prevState) => ({
                            ...prevState,
                            search,
                        }))
                    }
                />
            ),
        },
        {
            key: "status",
            label: "Status",
            tagLabel: status.label,
            onRemove: handleStatusRemove,
            filter: (
                <FilterNoSearchPolaris
                    data={STATUS_OPTIONS}
                    value={status}
                    onChange={({ value, label }) =>
                        setStatus((prevState) => ({
                            ...prevState,
                            value,
                            label,
                        }))
                    }
                    onChangeSearch={({ search }) =>
                        setStatus((prevState) => ({
                            ...prevState,
                            search,
                        }))
                    }
                />
            ),
        },
        {
            key: "suppliers",
            label: "Suppliers",
            tagLabel: supplier.label,
            onRemove: handleSupplierRemove,
            filter: (
                <FilterSingleValue
                    noAll
                    multiple
                    options={suppliers || []}
                    value={supplier.value || []}
                    onChange={(values) => {
                        const ids = [];
                        const label = [];
                        for (let item of values) {
                            if (!item || !item.value) continue;
                            ids.push(item.value);
                            label.push(item.label);
                        }

                        setSupplier((p) => ({ ...p, value: ids, label }));
                    }}
                />
            ),
        },
    ].filter(Boolean);

    const appliedFilters = [];
    for (let { key, label, onRemove, tagLabel } of filters) {
        if (!isEmpty(tagLabel) && tagLabel) {
            appliedFilters.push({
                key,
                label: disambiguateLabel(label, tagLabel),
                onRemove,
            });
        }
    }

    if (!isEmpty(team.value)) {
        const key = "team";
        appliedFilters.push({
            key,
            label: disambiguateLabel(key, team.label),
            onRemove: handleTeamRemove,
        });
    }

    return (
        <Filters
            filters={filters}
            appliedFilters={appliedFilters}
            queryValue={queryValue}
            queryPlaceholder={"Filter product base"}
            onQueryChange={handleQueryValueChange}
            onQueryClear={handleQueryValueRemove}
            onClearAll={handleFiltersClearAll}
        />
    );

    function disambiguateLabel(label, value) {
        if (!label) {
            return value;
        }

        if (label === "suppliers") {
            return `Suppliers: ${value.join(", ")}`;
        }

        return `${label}: ${value}`;
    }
};

function getSuppliers(data, isAdministrator) {
    let nodes = data?.teamUsersByRole || [];
    if (isAdministrator) {
        nodes = data?.users?.nodes || [];
    }
    const values = [];
    for (const node of nodes) {
        if (!node || !node.id) continue;

        let label = [node.firstName, node.lastName].join(" ");
        if (node.email) {
            label += ` (${node.email})`;
        }

        values.push({ value: node.id, label });
    }

    return values;
}
